// URL construction for the content area. This feature owns its route prefix, so anything
// that needs to link to a rendered page — including the admin Pages screens — builds the
// URL through here rather than hardcoding `/content`.
//
// Only the bare slug path (`slugFull`, e.g. `about/committee`) is persisted; the prefix is
// a routing concern that lives entirely on the frontend.

/**
 * Route prefix the content pages are served under.
 * Keep in sync with `routes.tsx` — change both together.
 */
export const CONTENT_PATH_PREFIX = 'content';

/**
 * The hub landing: the bare content prefix, `/content`. The hub does NOT own the site root —
 * the portal root `/` belongs to the host app, which sends it to `/admin`.
 */
export const HUB_HOME_PATH = `/${CONTENT_PATH_PREFIX}`;

const trimLeadingSlashes = (value: string | undefined): string => (value || '').replace(/^\/+/, '');

/** Join a host base with an app-relative path, without doubling the slash. */
const toAbsoluteUrl = (base: string, path: string): string =>
  `${(base || '').replace(/\/+$/, '')}${path}`;

/** App-relative route to a rendered page, e.g. `/content/about/committee`. */
export const buildPagePath = (slugFull: string | undefined): string =>
  `/${CONTENT_PATH_PREFIX}/${trimLeadingSlashes(slugFull)}`;

/** Route to the hub landing page, `/content`. */
export const buildHubHomePath = (): string => HUB_HOME_PATH;

/**
 * True when a pathname is the landing. Ignores a trailing slash and case, so the Home entry
 * stays lit on `/content/` too.
 */
export const isHubHomePath = (pathname: string): boolean =>
  pathname.replace(/(.)\/+$/, '$1').toLowerCase() === HUB_HOME_PATH;

/**
 * Absolute public URL for a page, e.g. `https://host/content/about/committee`.
 * Display only — this is what the admin screens show as a page's "Path URL".
 */
export const buildFullUrl = (base: string, slugFull: string | undefined): string =>
  toAbsoluteUrl(base, buildPagePath(slugFull));

/** Normalises a slug captured from the URL: strips wrapping slashes and lowercases. */
export const normalizeSlug = (raw: string | undefined): string =>
  (raw || '').replace(/^\/+|\/+$/g, '').toLowerCase();

// ─────────────────────────────────────────────────────────────────────────────
// Newsletters. These live on their own prefix (association-specific content), kept OFF
// `/content/*` so a newsletter slug can never collide with a content-page slug.
// ─────────────────────────────────────────────────────────────────────────────

/** Route prefix the newsletter archive is served under. Keep in sync with `routes.tsx`. */
export const NEWSLETTER_PATH_PREFIX = 'newsletters';

/** App-relative route to the newsletter archive, e.g. `/newsletters`. */
export const buildNewsletterArchivePath = (): string => `/${NEWSLETTER_PATH_PREFIX}`;

/** App-relative route to a single newsletter, e.g. `/newsletters/bulletin-vol-12`. */
export const buildNewsletterPath = (slugOrId: string | undefined): string =>
  `/${NEWSLETTER_PATH_PREFIX}/${trimLeadingSlashes(slugOrId)}`;

// ─────────────────────────────────────────────────────────────────────────────
// Articles and news. Peers of the newsletter archive, each on its own prefix for the same
// reason: an article slug must never be able to collide with a content-page slug.
// ─────────────────────────────────────────────────────────────────────────────

/** Route prefix the article library is served under. Keep in sync with `routes.tsx`. */
export const ARTICLE_PATH_PREFIX = 'articles';

/** App-relative route to the article library, e.g. `/articles`. */
export const buildArticleArchivePath = (): string => `/${ARTICLE_PATH_PREFIX}`;

/** App-relative route to a single article, e.g. `/articles/reloading-your-308`. */
export const buildArticlePath = (slug: string | undefined): string =>
  `/${ARTICLE_PATH_PREFIX}/${trimLeadingSlashes(slug)}`;

/** Absolute public URL for an article. Display only — the admin form's "Path URL". */
export const buildArticleUrl = (base: string, slug: string | undefined): string =>
  toAbsoluteUrl(base, buildArticlePath(slug));

/** Route prefix the news feed is served under. Keep in sync with `routes.tsx`. */
export const NEWS_PATH_PREFIX = 'news';

/** App-relative route to the news feed, e.g. `/news`. */
export const buildNewsArchivePath = (): string => `/${NEWS_PATH_PREFIX}`;

/** App-relative route to a single news item, e.g. `/news/bek-en-klouseer-outbreak`. */
export const buildNewsPath = (slug: string | undefined): string =>
  `/${NEWS_PATH_PREFIX}/${trimLeadingSlashes(slug)}`;

/** Absolute public URL for a news item. Display only — the admin form's "Path URL". */
export const buildNewsUrl = (base: string, slug: string | undefined): string =>
  toAbsoluteUrl(base, buildNewsPath(slug));

// ─────────────────────────────────────────────────────────────────────────────
// The one host-app route the hub links back to: the member portal home. Kept as a single
// constant so a lift into another project changes it in exactly one place.
// ─────────────────────────────────────────────────────────────────────────────

/** App-relative route to the member portal home (outside the hub). */
export const MEMBER_PORTAL_PATH = '/admin/dashboard';
